import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LogOut, Menu, Compass, Files, LayoutDashboard, GraduationCap, DollarSign, ShieldCheck, Share2 } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const linkBase = "px-3 py-2 rounded-full text-sm font-medium transition-colors";
const linkClass = ({ isActive }) =>
  `${linkBase} ${isActive ? "bg-neutral-900 text-white" : "text-neutral-700 hover:text-neutral-900 hover:bg-neutral-100"}`;

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = () => { logout(); nav("/"); };

  const items = user
    ? [
        { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/grants", label: "Grants", icon: Compass },
        { to: "/applications", label: "Applications", icon: Files },
        { to: "/social", label: "Social", icon: Share2 },
        { to: "/academy", label: "Academy", icon: GraduationCap },
        ...(user.is_admin ? [{ to: "/admin", label: "Admin", icon: ShieldCheck }] : []),
      ]
    : [
        { to: "/grants", label: "Grants", icon: Compass },
        { to: "/academy", label: "Academy", icon: GraduationCap },
        { to: "/pricing", label: "Pricing", icon: DollarSign },
        { to: "/about", label: "About", icon: Compass },
      ];

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-neutral-200" data-testid="app-navbar">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2" data-testid="nav-logo">
          <div className="h-8 w-8 rounded-md bg-neutral-900 flex items-center justify-center text-white font-display font-bold">G</div>
          <span className="font-display text-lg font-semibold tracking-tight">GrantFlow</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} className={linkClass} data-testid={`nav-${it.label.toLowerCase()}`}>
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              {(() => {
                const s = user.subscription_status;
                if (s === "active") return <span className="text-xs px-2.5 py-1 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-700" data-testid="nav-subscription-badge">Pro</span>;
                if (s === "trialing") {
                  const days = user.trial_ends_at ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / (1000*60*60*24))) : 0;
                  return <span className="text-xs px-2.5 py-1 rounded-full border border-blue-300 bg-blue-50 text-blue-700" data-testid="nav-subscription-badge">Trial · {days}d</span>;
                }
                return <span className="text-xs px-2.5 py-1 rounded-full border border-amber-300 bg-amber-50 text-amber-700" data-testid="nav-subscription-badge">Free</span>;
              })()}
              <Link to="/dashboard" className="btn-ghost text-sm" data-testid="nav-account">{user.name?.split(" ")[0] || "Account"}</Link>
              <button onClick={handleLogout} className="btn-primary text-sm" data-testid="nav-logout">
                <LogOut size={16} /> Log out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-ghost text-sm" data-testid="nav-login">Log in</Link>
              <Link to="/signup" className="btn-primary text-sm" data-testid="nav-signup">Start free</Link>
            </>
          )}
        </div>

        <Sheet>
          <SheetTrigger asChild className="md:hidden">
            <button className="p-2 rounded-full border border-neutral-200" aria-label="menu" data-testid="mobile-menu-btn">
              <Menu size={20} />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-72">
            <div className="mt-8 flex flex-col gap-2">
              {items.map((it) => (
                <NavLink key={it.to} to={it.to} className={linkClass} data-testid={`m-nav-${it.label.toLowerCase()}`}>
                  {it.label}
                </NavLink>
              ))}
              {user ? (
                <button onClick={handleLogout} className="mt-3 btn-primary" data-testid="m-nav-logout">Log out</button>
              ) : (
                <>
                  <Link to="/login" className="mt-3 btn-ghost" data-testid="m-nav-login">Log in</Link>
                  <Link to="/signup" className="btn-primary" data-testid="m-nav-signup">Start free</Link>
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
